import type { CustomFile } from '../types';

import type { TypeUploadConfig, TypeUploadResult } from './types';
import type { TypeUploadContext } from './createUploadContext';

import runWithConcurrencyUpload, {
    type TypeRunWithConcurrencyUploadOutcome,
} from '../../async/runWithConcurrencyUpload';
import createUploadProgress from './createUploadProgress';
import createRequestRegistry from './createRequestRegistry';
import uploadOnceFile from './uploadOnceFile';
import resolveUploadConfig from './core/resolveUploadConfig';
import composeAttempt from './core/composeAttempt';
import classifyOutcome from './core/classifyOutcome';
import shouldSend from './core/shouldSend';
import createFeatures from './features';

// ----------------------------------------------------------------------------

/*
 * The engine for per-file uploads: one request per file, as many at a time as the `concurrency`
 * block allows, funnelling every outcome into the success/error/skipped buckets while keeping the
 * shared context's counters and the progress emitter in step.
 *
 * What used to be three modes is this one function: parallel is `max: 4`, limited is `max: N`,
 * sequential is `max: 1`. They differed only in which number reached this line.
 */
async function uploadFiles(
    files: CustomFile[],
    config: TypeUploadConfig,
    context: TypeUploadContext,
): Promise<TypeUploadResult> {
    const success: TypeUploadResult['success'] = [];
    const error: TypeUploadResult['error'] = [];
    const skipped: TypeUploadResult['skipped'] = [];

    context.totalFiles = files.length;

    if (!context.totalBytes) {
        context.totalBytes = files.reduce((sum, f) => sum + (f.size ?? 0), 0);
    }

    const features = resolveUploadConfig(config);

    const progress = createUploadProgress(files, config, context, features);
    const registry = createRequestRegistry(context, progress.emit);

    context.abortAll = registry.abortAll;

    // ------------------------------------------------------------------------

    const concurrency = features.concurrency.enabled ? features.concurrency.max : files.length;

    const isAborted = (): boolean => context.abortedFlag.aborted;
    const waitForResume = async (): Promise<void> => context.pausedFlag.waitForResume();

    const worker = async (file: CustomFile): Promise<TypeRunWithConcurrencyUploadOutcome> => {
        await context.pausedFlag.waitForResume();

        if (context.abortedFlag.aborted) {
            return 'aborted';
        }

        try {
            /*
             * Built per file so a feature can keep per-file state, and composed here rather than
             * called directly: whichever of filter/retry/stall/… the config enabled is already in.
             */
            const plugins = createFeatures(features, {
                isAborted,
                waitForResume,
                file,
                fnPrepareFile: config.fnPrepareFile,
                fnRetry: (attempt, result) => {
                    config.fnFileRetry?.(file, attempt, result.message ?? `Upload failed (HTTP ${result.status ?? 0})`);
                },
            });

            if (!(await shouldSend(plugins, file))) {
                context.completedFiles.value += 1;
                context.skippedCount += 1;
                // Terminal for this file: account its bytes so total progress can still reach 100%.
                progress.markDone(file);
                progress.emit();

                skipped.push({ file });

                return 'skipped';
            }

            const attempt = composeAttempt(
                async (part) => uploadOnceFile(file, { config, features, plugins, context, progress, registry, part }),
                plugins,
                { isAborted, waitForResume },
            );

            const result = await attempt();
            const outcome = classifyOutcome(result, isAborted, (xhr) => registry.isAborted(xhr));

            if (outcome === 'ok') {
                context.successCount += 1;
                context.completedFiles.value += 1;
                progress.emit();

                success.push({ file, xhr: result.xhr });
                config.fnFileSuccess?.(file, result.xhr);

                return 'ok';
            }

            if (outcome === 'aborted') {
                return 'aborted';
            }

            context.errorCount += 1;
            context.completedFiles.value += 1;
            progress.markDone(file);
            progress.emit();

            const lastStatus = result.status ?? 0;
            const lastMessage = result.message;
            const errMessage = lastMessage ?? (lastStatus ? `Upload failed (HTTP ${lastStatus})` : 'Upload failed');
            error.push({ file, err: errMessage });
            config.fnFileError?.(file, errMessage);

            return 'error';
        } catch (err: any) {
            if (context.abortedFlag.aborted) {
                return 'aborted';
            }

            context.errorCount += 1;
            context.completedFiles.value += 1;
            progress.markDone(file);
            progress.emit();

            const errMessage = err?.message ?? 'Unknown error';
            error.push({ file, err: errMessage });
            config.fnFileError?.(file, errMessage);

            return 'error';
        }
    };

    const runOptions = {
        shouldStop: (): boolean => context.abortedFlag.aborted,
        stopOnError: features.concurrency.stopOnError,
    };

    if (features.concurrency.gateFirst && files.length > 1) {
        /*
         * Gate the first file (it settles the server-side parent record), then
         * fan the rest out at the configured concurrency once it has landed.
         */
        const gateResult = await runWithConcurrencyUpload([files[0]], 1, worker, runOptions);
        // stopOnError is scoped to a single run, so a failed gate has to be re-checked here.
        const gateFailed = gateResult.errorCount > 0;
        const stopRun = context.abortedFlag.aborted || (gateFailed && runOptions.stopOnError);

        if (!stopRun) {
            /*
             * A failed gate means the server-side parent record may not exist yet, so fanning
             * the rest out at full concurrency would recreate the very race the gate prevents.
             * They are still attempted, but one at a time so whichever request wins creates it.
             */
            await runWithConcurrencyUpload(files.slice(1), gateFailed ? 1 : concurrency, worker, runOptions);
        }
    } else {
        await runWithConcurrencyUpload(files, concurrency, worker, runOptions);
    }

    progress.flush();

    /*
     * Whatever the runs never classified was never reached — the run was aborted, or stopOnError
     * cut it short. Reporting it keeps the four buckets adding up to the files handed in.
     */
    const accounted = new Set<CustomFile>([
        ...success.map((s) => s.file),
        ...error.map((e) => e.file),
        ...skipped.map((s) => s.file),
    ]);
    const notAttempted = files.filter((file) => !accounted.has(file)).map((file) => ({ file }));

    return { success, error, skipped, notAttempted, aborted: context.abortedFlag.aborted };
}

// ----------------------------------------------------------------------------

export default uploadFiles;
